/** Tenant paneli ile uyumlu ortak çalışma grubu stilleri */

import { PANEL, PANEL_PRIMITIVE, getPanelStepRank } from "./panelTokens";

import { panelPageTitleSx } from "./panelPageHeaderSx";

export const panelCollaborationTitleSx = () => ({
  ...panelPageTitleSx,
});

export const panelCollaborationSubtitleSx = {
  color: PANEL.text.muted,
  mt: 0.5,
};

export const panelCollaborationGuidePaperSx = {
  p: 3,
  borderRadius: 3,
  backgroundColor: PANEL.fill.muted,
  border: `1px solid ${PANEL.border}`,
  mb: 3,
};

export const panelCollaborationGuideTitleSx = {
  fontWeight: 700,
  color: PANEL.text.primary,
  mb: 1.5,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

export const panelCollaborationGuideBodySx = {
  color: PANEL.text.meta,
};

export const panelGuideStepListSx = {
  m: 0,
  p: 0,
  listStyle: "none",
  display: "flex",
  flexDirection: "column",
  gap: 0.85,
};

export const panelGuideStepRowSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  m: 0,
};

export const panelGuideStepBadgeSx = (index = 0) => {
  const rank = getPanelStepRank(index);
  return {
    width: 22,
    height: 22,
    borderRadius: "7px",
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    mt: "1px",
    bgcolor: rank.soft,
    color: rank.text,
    border: `1px solid ${rank.border}`,
    fontSize: "0.68rem",
    fontWeight: 800,
    lineHeight: 1,
  };
};

export const panelGuideStepTextSx = (index = 0) => {
  const rank = getPanelStepRank(index);
  return {
    color: PANEL.text.meta,
    fontSize: "0.8125rem",
    lineHeight: 1.45,
    minWidth: 0,
    "& strong, & b": {
      color: rank.text,
      fontWeight: 700,
    },
  };
};

export const panelRankedSurfaceSx = (index = 0) => {
  const rank = getPanelStepRank(index);
  return {
    border: `1px solid ${rank.border}`,
    borderLeft: `3px solid ${rank.main}`,
    bgcolor: rank.soft,
  };
};

export const panelCollaborationCardSx = () => ({
  background: PANEL.surface,
  border: `1px solid ${PANEL.border}`,
  borderRadius: PANEL.radius.cardMd,
  height: "100%",
  boxShadow: PANEL.shadow.card,
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    borderColor: PANEL.fill.hover,
  },
});

export const panelCollaborationFormPaperSx = () => ({
  p: 3,
  borderRadius: PANEL.radius.cardMd,
  maxWidth: 720,
  background: PANEL.surface,
  border: `1px solid ${PANEL.border}`,
  boxShadow: PANEL.shadow.card,
});

export const panelCollaborationPrimaryButtonSx = () => ({
  background: PANEL.accent.main,
  color: PANEL.accent.contrast,
  px: 3,
  py: 1.2,
  borderRadius: PANEL.radius.input,
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    background: PANEL.accent.hover,
    color: PANEL.accent.contrast,
    boxShadow: PANEL.accent.shadow25,
  },
  "& .MuiButton-startIcon": { color: "inherit" },
  transition: "all 0.15s ease",
});

/** Boş liste / henüz kayıt yok durumu */
export const panelCollaborationEmptyStateBoxSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  py: 6,
  px: 3,
  borderRadius: PANEL.radius.card,
  bgcolor: PANEL.surface,
  border: `1px solid ${PANEL.border}`,
  boxShadow: PANEL.shadow.card,
};

export const panelCollaborationEmptyStateIconWrapSx = {
  width: 72,
  height: 72,
  borderRadius: PANEL.radius.card,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: PANEL.accent.soft,
  border: `1px solid ${PANEL.accent.softBorder22}`,
  mb: 2.5,
};

export const panelCollaborationEmptyStateTitleSx = {
  color: PANEL.text.primary,
  fontWeight: 700,
  mb: 1,
  letterSpacing: -0.2,
};

export const panelCollaborationEmptyStateHintSx = {
  color: PANEL.text.muted,
  mb: 3,
  maxWidth: 480,
  lineHeight: 1.6,
};

export const panelCollaborationTextFieldSx = () => ({
  "& .MuiInputLabel-root": { color: PANEL.text.muted },
  "& .MuiInputLabel-root.Mui-focused": { color: PANEL.accent.focus },
  "& .MuiOutlinedInput-root": {
    color: PANEL.text.primary,
    backgroundColor: PANEL.surface,
    borderRadius: PANEL.radius.input,
    "& fieldset": { borderColor: PANEL.border },
    "&:hover fieldset": { borderColor: PANEL.fill.hover },
    "&.Mui-focused fieldset": { borderColor: PANEL.accent.main },
  },
  "& .MuiFormHelperText-root": { color: PANEL.text.label },
});

export const panelCollaborationDoctorChipSx = () => ({
  bgcolor: PANEL.accent.soft12,
  color: PANEL.accent.text,
  fontWeight: 600,
  border: `1px solid ${PANEL.accent.softBorder}`,
  "& .MuiChip-deleteIcon": {
    color: PANEL.accent.text,
    fontSize: 20,
    opacity: 0.92,
    "&:hover": {
      color: PANEL.danger.text,
      opacity: 1,
    },
  },
});

/** Tenant modal — açık zemin üzerinde okunabilir renkler */
export const panelCollaborationModalLight = {
  sectionBg: PANEL.fill.muted,
  sectionBorder: `1px solid ${PANEL.border}`,
  title: PANEL.text.title,
  body: PANEL.text.meta,
  email: PANEL.text.muted,
  muted: PANEL.text.label,
  divider: PANEL.border,
  chipOwnerBg: PANEL.blue.chipBg,
  chipOwnerColor: PANEL.blue.text,
  chipMemberBg: PANEL.pageBg,
  chipMemberColor: PANEL.text.meta,
  avatarMember: PANEL.blue.main,
  avatarFile: PANEL_PRIMITIVE.emerald600,
};

/** @deprecated tenant* — geriye uyumluluk alias'ları */
export const tenantTitleSx = panelCollaborationTitleSx;
export const tenantSubtitleSx = panelCollaborationSubtitleSx;
export const tenantGuidePaperSx = panelCollaborationGuidePaperSx;
export const tenantGuideTitleSx = panelCollaborationGuideTitleSx;
export const tenantGuideBodySx = panelCollaborationGuideBodySx;
export const tenantCardSx = panelCollaborationCardSx;
export const tenantFormPaperSx = panelCollaborationFormPaperSx;
export const tenantPrimaryButtonSx = panelCollaborationPrimaryButtonSx;
export const tenantEmptyStateBoxSx = panelCollaborationEmptyStateBoxSx;
export const tenantEmptyStateIconWrapSx = panelCollaborationEmptyStateIconWrapSx;
export const tenantEmptyStateTitleSx = panelCollaborationEmptyStateTitleSx;
export const tenantEmptyStateHintSx = panelCollaborationEmptyStateHintSx;
export const tenantTextFieldSx = panelCollaborationTextFieldSx;
export const tenantDoctorChipSx = panelCollaborationDoctorChipSx;
export const tenantModalLight = panelCollaborationModalLight;
